/**
 * AccountSettingsForm - An organism for editing user profile settings.
 *
 * Displays read-only fields (email, account status) and an editable name field.
 * Save button is disabled when the form is clean.
 */

import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2, CheckCircle } from 'lucide-react';
import { Button } from '@lean-agility/design-system/react';
import { Input } from '@lean-agility/design-system/react';
import { InlineError } from '@lean-agility/design-system/react';
import { profileSchema, type ProfileFormData } from '../../validation/authSchemas';
import type { AuthUser } from '../../types';

export interface AccountSettingsFormProps {
  user: AuthUser;
  onSubmit: (data: { name?: string | null }) => Promise<void>;
  isLoading?: boolean;
  isSuccess?: boolean;
  error?: string | null;
  onDismissError?: () => void;
}

function formatExpiryDate(expiryDate: string | null | undefined): string {
  if (!expiryDate) return 'Lifetime access';
  const date = new Date(expiryDate);
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

export function AccountSettingsForm({
  user,
  onSubmit,
  isLoading = false,
  isSuccess = false,
  error,
  onDismissError,
}: AccountSettingsFormProps) {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting, isDirty },
  } = useForm<ProfileFormData>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      name: user.name ?? '',
    },
  });

  const handleFormSubmit = async (data: ProfileFormData) => {
    const name = data.name?.trim();
    await onSubmit({ name: name === '' ? null : name });
    reset({ name: name ?? '' });
  };

  const isFormLoading = isLoading || isSubmitting;

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} noValidate>
      <div className="space-y-4">
        {error && <InlineError error={error} dismissible onDismiss={onDismissError} />}

        <Input label="Email" value={user.email} disabled />

        <Input
          {...register('name')}
          label="Display name"
          placeholder="Enter your name"
          error={errors.name?.message}
          disabled={isFormLoading}
        />

        <Input label="Account status" value={formatExpiryDate(user.expiry_date)} disabled />

        <div className="flex items-center justify-end gap-2 pt-2">
          {isSuccess && !isDirty && (
            <span className="flex items-center gap-1.5 text-sm text-feedback-success">
              <CheckCircle className="size-4" />
              Saved
            </span>
          )}
          <Button type="submit" variant="primary" size="md" disabled={isFormLoading || !isDirty}>
            {isFormLoading ? (
              <>
                <Loader2 className="size-4 animate-spin" />
                Saving...
              </>
            ) : (
              'Save'
            )}
          </Button>
        </div>
      </div>
    </form>
  );
}
